import React, { useEffect, useState } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { InputText } from "primereact/inputtext";
import { Password } from "primereact/password";
import { Button } from "primereact/button";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import axios from "axios";
import { SetupWizardDialog } from "../../../components/Setup/SetupWizard";
import "../../styles/login.scss";

const Login = ({ onClose, redirectTo = "/index/dashboard" }) => {
  const [showSetupDialog, setShowSetupDialog] = useState(false);
  const [formData, setFormData] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [resetLoading, setResetLoading] = useState(false);
  // The login page runs before any session exists, so the school's own logo
  // comes from the public branding endpoint (resolved by hostname) rather
  // than from school settings. Null means "not a school domain" — fall back.
  const [branding, setBranding] = useState(null);
  const { login } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    let alive = true;
    axios
      .get("/api/branding")
      .then((res) => alive && setBranding(res.data?.branding || null))
      .catch(() => {}); // branding must never block signing in
    return () => {
      alive = false;
    };
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    login(formData).then((response) => {
      setLoading(false);
      if (response.success) {
        toast.success(response.msg, { autoClose: 2000 });
        const dest = response.user?.roleName === "SuperAdmin"
          ? "/platform/dashboard"
          : redirectTo;
        navigate(dest);
        if (onClose) onClose();
      } else {
        toast.error(response.msg, { autoClose: 2000 });
      }
    }).catch(() => setLoading(false));
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setResetLoading(true);
    try {
      const response = await axios.post("/api/auth/forgot-password", { email: resetEmail });
      if (response.data.success) {
        toast.success(response.data.msg, { autoClose: 3000 });
        setShowForgotPassword(false);
        setResetEmail("");
      } else {
        toast.error(response.data.msg, { autoClose: 3000 });
      }
    } catch (error) {
      toast.error(error.response?.data?.msg || "Error sending reset email", { autoClose: 3000 });
    } finally {
      setResetLoading(false);
    }
  };

  const features = [
    { icon: "pi-users", title: "Student Management", desc: "Track enrollment, attendance & performance" },
    { icon: "pi-calendar", title: "Attendance Tracking", desc: "Daily attendance with reports & analytics" },
    { icon: "pi-wallet", title: "Fee Management", desc: "Generate vouchers & track payments" },
    { icon: "pi-chart-bar", title: "Dashboard Analytics", desc: "Visual insights for better decisions" },
  ];

  return (
    <div className="login-page">
      {/* Left Panel - Branding */}
      <div className="login-branding">
        <div className="branding-content">
          <div className="brand-header">
            <i className="pi pi-graduation-cap brand-icon"></i>
            <h1>Schoolwise</h1>
            <p className="tagline">Empowering Education Through Technology</p>
          </div>

          <div className="features-grid">
            {features.map((feature, index) => (
              <div key={index} className="feature-card">
                <div className="feature-icon">
                  <i className={`pi ${feature.icon}`}></i>
                </div>
                <div className="feature-text">
                  <h3>{feature.title}</h3>
                  <p>{feature.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right Panel - Login Form */}
      <div className="login-form-panel">
        <div className="login-form-container">
          <div className="form-header">
            <img
              alt={branding?.schoolName ? `${branding.schoolName} logo` : "logo"}
              src={branding?.logo || "/site_logo.png"}
              height="80"
              className="logo"
              onError={(e) => { e.currentTarget.src = "/site_logo.png"; }}
            />
            <h2>{showForgotPassword ? "Reset Password" : "Welcome Back"}</h2>
            <p>{showForgotPassword
              ? "Enter your email to receive a reset link"
              : "Sign in to continue to your dashboard"}</p>
          </div>

          {!showForgotPassword ? (
            <form onSubmit={handleSubmit} className="login-form">
              <div className="form-field">
                <label htmlFor="email">
                  <i className="pi pi-envelope"></i> Email Address
                </label>
                <InputText
                  id="email"
                  name="email"
                  type="text"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="Enter your email"
                  required
                  className="w-full"
                />
              </div>

              <div className="form-field">
                <label htmlFor="password">
                  <i className="pi pi-lock"></i> Password
                </label>
                <Password
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  placeholder="Enter your password"
                  required
                  feedback={false}
                  toggleMask
                  style={{ width: '100%' }}
                  inputStyle={{ width: '100%' }}
                  pt={{ iconField: { style: { width: '100%' } } }}
                />
              </div>

              <div className="form-options">
                <a
                  href="#"
                  className="forgot-link"
                  onClick={(e) => { e.preventDefault(); setShowForgotPassword(true); }}
                >
                  Forgot Password?
                </a>
              </div>

              <Button
                type="submit"
                label={loading ? "Signing in..." : "Sign In"}
                icon={loading ? "pi pi-spin pi-spinner" : "pi pi-sign-in"}
                className="login-btn"
                disabled={loading}
              />
            </form>
          ) : (
            <form onSubmit={handleForgotPassword} className="login-form">
              <div className="form-field">
                <label htmlFor="resetEmail">
                  <i className="pi pi-envelope"></i> Email Address
                </label>
                <InputText
                  id="resetEmail"
                  type="email"
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  placeholder="Enter your registered email"
                  required
                  className="w-full"
                  autoFocus
                />
              </div>

              <Button
                type="submit"
                label={resetLoading ? "Sending..." : "Send Reset Link"}
                icon={resetLoading ? "pi pi-spin pi-spinner" : "pi pi-send"}
                className="login-btn"
                disabled={resetLoading}
              />

              <div className="form-options" style={{ justifyContent: "center", marginTop: "1rem" }}>
                <a
                  href="#"
                  className="forgot-link"
                  onClick={(e) => { e.preventDefault(); setShowForgotPassword(false); }}
                >
                  <i className="pi pi-arrow-left" style={{ marginRight: "0.5rem" }}></i>
                  Back to Sign In
                </a>
              </div>
            </form>
          )}

          <div className="form-footer">
            <p>
              Don't have an account?{" "}
              <a
                href="#"
                className="register-link"
                onClick={(e) => { e.preventDefault(); setShowSetupDialog(true); }}
              >
                Register your school
              </a>
            </p>
          </div>

          <SetupWizardDialog
            visible={showSetupDialog}
            onHide={() => setShowSetupDialog(false)}
          />

          <div className="form-divider">
            <span>Secure Login</span>
          </div>

          <div className="security-badges">
            <div className="badge">
              <i className="pi pi-shield"></i>
              <span>SSL Secured</span>
            </div>
            <div className="badge">
              <i className="pi pi-lock"></i>
              <span>Data Protected</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
